/**
 * Activity Log Service
 * Handles HIPAA-compliant event logging to ActivityLog service
 */

import { getServiceApiUrl } from '../utils/constants';

function resolveActivityLogApiUrl() {
  return getServiceApiUrl('ACTIVITY_LOG');
}

/**
 * Get client IP address (best effort)
 */
const getClientIP = () => {
  return null;
};

/**
 * Get user agent
 */
const getUserAgent = () => {
  return navigator.userAgent || 'Unknown';
};

/**
 * Get session ID from localStorage or generate one
 */
const getSessionId = () => {
  let sessionId = localStorage.getItem('activity_log_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('activity_log_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Log an activity event
 */
export const logActivityEvent = async (eventData, organizationId, token) => {
  if (!organizationId || !token) {
    return;
  }

  const base = resolveActivityLogApiUrl();
  if (!base) {
    // Remote shell without Activity Log proxy — skip (non-blocking).
    return;
  }

  try {
    const payload = {
      event_type: eventData.event_type || 'general',
      event_category: eventData.event_category || 'system',
      event_description: eventData.event_description || 'Activity event',
      actor_type: eventData.actor_type || 'user',
      actor_name: eventData.actor_name || 'Unknown User',
      actor_id: eventData.actor_id || 'unknown',
      application_id: eventData.application_id || 'agentic-rcm',
      ip_address: eventData.ip_address || getClientIP(),
      user_agent: eventData.user_agent || getUserAgent(),
      session_id: eventData.session_id || getSessionId(),
      event_metadata: eventData.event_metadata || {},
      severity: eventData.severity || 'info',
      ...(eventData.duration_ms && { duration_ms: eventData.duration_ms }),
      ...(eventData.request_id && { request_id: eventData.request_id }),
      ...(eventData.correlation_id && { correlation_id: eventData.correlation_id }),
    };

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 4000) : null;

    const response = await fetch(
      `${base}/api/activity-events?organization_id=${organizationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        signal: controller?.signal,
      }
    );

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      return;
    }
  } catch {
    // Network / timeout — never block login or navigation / spam console
  }
};

/**
 * Log user sign-in event
 */
export const logSignIn = async (user, organizationId, token) => {
  await logActivityEvent({
    event_type: 'sign_in',
    event_category: 'login',
    event_description: `User ${user.email || user.id} signed in`,
    actor_type: 'user',
    actor_name: user.full_name || user.first_name || user.email || 'Unknown User',
    actor_id: user.id || 'unknown',
    application_id: 'agentic-rcm',
    severity: 'info',
  }, organizationId, token);
};

/**
 * Log user sign-out event
 */
export const logSignOut = async (user, organizationId, token) => {
  await logActivityEvent({
    event_type: 'sign_out',
    event_category: 'logout',
    event_description: `User ${user.email || user.id} signed out`,
    actor_type: 'user',
    actor_name: user.full_name || user.first_name || user.email || 'Unknown User',
    actor_id: user.id || 'unknown',
    application_id: 'agentic-rcm',
    severity: 'info',
  }, organizationId, token);
};

/**
 * Log HIPAA audit event (PHI access)
 */
export const logHipaaAudit = async (action, resource, resourceId, user, organizationId, token, metadata = {}) => {
  await logActivityEvent({
    event_type: 'hipaa_audit',
    event_category: action,
    event_description: `HIPAA audit: ${action} ${resource}${resourceId ? ` (ID: ${resourceId})` : ''}`,
    actor_type: 'user',
    actor_name: user.full_name || user.first_name || user.email || 'Unknown User',
    actor_id: user.id || 'unknown',
    application_id: 'agentic-rcm',
    event_metadata: {
      resource,
      resource_id: resourceId,
      ...metadata,
    },
    severity: 'warning',
  }, organizationId, token);
};

/**
 * Log data access event
 */
export const logDataAccess = async (resource, action, user, organizationId, token, metadata = {}) => {
  await logActivityEvent({
    event_type: 'data_access',
    event_category: action,
    event_description: `Data access: ${action} ${resource}`,
    actor_type: 'user',
    actor_name: user.full_name || user.first_name || user.email || 'Unknown User',
    actor_id: user.id || 'unknown',
    application_id: 'agentic-rcm',
    event_metadata: {
      resource,
      ...metadata,
    },
    severity: 'info',
  }, organizationId, token);
};

/**
 * Log service access event
 */
export const logServiceAccess = async (serviceName, action, user, organizationId, token, metadata = {}) => {
  await logActivityEvent({
    event_type: 'service_access',
    event_category: action,
    event_description: `Service access: ${action} ${serviceName}`,
    actor_type: 'user',
    actor_name: user.full_name || user.first_name || user.email || 'Unknown User',
    actor_id: user.id || 'unknown',
    application_id: 'agentic-rcm',
    event_metadata: {
      service_name: serviceName,
      ...metadata,
    },
    severity: 'info',
  }, organizationId, token);
};
